import { WeightQuantity, WeightUnit } from "@/types";

// Helpers for a final weight typed in kg or tonnes. The weight goes to the backend as the number the user typed
// together with its unit, so the helpers never turn tonnes into kg to send them.

/** The form value of a weight field. Mantine gives a string while the field is empty or half-typed ("5,"). */
export interface WeightInputValue {
  amount: number | string;
  unit: WeightUnit;
}

/** What is wrong with a typed weight, if anything. The field shows one message per issue. */
export type WeightIssue = "required" | "notPositive" | "notWholeKg" | "tooLarge";

/** Whole kg are stored as a 32-bit integer, so a typo with too many digits would overflow. */
export const MAX_WEIGHT_KG = 2_147_483_647;

/** The validation message of each issue. Written out so the translation extractor sees every key. */
export const WEIGHT_ISSUE_KEYS: Record<WeightIssue, string> = {
  required: "validation:weight.required",
  notPositive: "validation:weight.notPositive",
  notWholeKg: "validation:weight.notWholeKg",
  tooLarge: "validation:weight.tooLarge",
};

export const WEIGHT_UNIT_LABELS: Record<WeightUnit, string> = { KG: "kg", TONNES: "t" };

/** The weight unit of an order's unit name ("kg", "Tonn"), or null when the order isn't counted by weight. */
export const weightUnitOf = (orderUnit: string | undefined): WeightUnit | null => {
  switch (orderUnit?.toLowerCase()) {
    case "kg":
      return "KG";
    case "tonn":
      return "TONNES";
    default:
      return null;
  }
};

/** Reads a typed number such as "1 000,5". Returns null when it is empty or isn't a number. */
export const parseWeightAmount = (amount: number | string): number | null => {
  const text = typeof amount === "number" ? String(amount) : amount.replace(/\s/g, "").replace(",", ".");
  if (text === "") return null;
  const number = Number(text);
  return Number.isFinite(number) ? number : null;
};

/**
 * The weight in kg, or null while the field is empty or isn't a number. Exact, and fractional when the tonnes
 * don't come out as whole kg: tonnes are converted by moving the decimal point, because 1.001 * 1000 gives
 * 1000.9999999999999.
 */
export const toKg = (value: WeightInputValue): number | null => {
  const amount = parseWeightAmount(value.amount);
  if (amount === null) return null;
  if (value.unit === "KG") return amount;

  const text = String(amount);
  // Exponent notation only appears far outside any valid weight, where the float error doesn't matter.
  if (text.includes("e")) return amount * 1000;
  const [integer, fraction = ""] = text.split(".");
  const rest = fraction.length > 3 ? `.${fraction.slice(3)}` : "";
  return Number(`${integer}${fraction.slice(0, 3).padEnd(3, "0")}${rest}`);
};

/** The first thing wrong with a typed weight, or null when it can be sent. */
export const validateWeight = (value: WeightInputValue): WeightIssue | null => {
  const kg = toKg(value);
  if (kg === null) return "required";
  if (kg <= 0) return "notPositive";
  if (!Number.isInteger(kg)) return "notWholeKg";
  if (kg > MAX_WEIGHT_KG) return "tooLarge";
  return null;
};

/** The weight as the backend takes it: the number as typed and its unit. The caller validates first. */
export const toWeightQuantity = (value: WeightInputValue): WeightQuantity => ({
  // An unvalidated, empty value becomes 0, which the backend rejects as an invalid quantity.
  amount: parseWeightAmount(value.amount) ?? 0,
  unit: value.unit,
});

/** Whether the typed weight is the same number in the same unit. 5 000 kg is not the same as 5 t. */
export const isSameWeight = (value: WeightInputValue, quantity: WeightQuantity): boolean =>
  parseWeightAmount(value.amount) === quantity.amount && value.unit === quantity.unit;
